<template>
  <div class="editor">
    <div class="editor-heading"><div><div class="eyebrow">FICHE BADGE</div><h2>Modifier le badge</h2><p>Informations et affectation</p></div><button class="close-button" type="button" aria-label="Fermer l'édition" :disabled="saving" @click="$emit('cancel')">×</button></div>
    <form @submit.prevent="save">
      <div class="read-only"><span>Numéro</span><strong>{{ badge.number || 'Non renseigné' }}</strong></div>
      <div class="read-only"><span>Slot balise</span><strong>{{ badge.slot != null ? badge.slot : 'Non défini' }}</strong></div>
      <div class="read-only"><span>Dernière activité</span><strong>{{ formatLastActivity(badge.lastActivity) }}</strong></div>
      <label class="field"><span>Référence du badge</span><input v-model="form.ref" type="text" maxlength="255" :disabled="saving" placeholder="Référence du badge" /></label>
      <label class="field"><span>Statut du badge</span><select v-model="form.status" :disabled="saving"><option value="NEW">À valider</option><option value="ACTIVE">Actif</option><option value="DISABLED">Désactivé</option></select></label>
      <div class="actions"><button class="primary-button" type="submit" :disabled="saving || !changed">{{ saving ? 'Enregistrement…' : 'Enregistrer les modifications' }}</button></div>
    </form>
    <section class="association">
      <h3>Chauffeur associé</h3>
      <p class="helper">Un badge ne peut être attribué qu'à un chauffeur à la fois.</p>
      <template v-if="badge.driverId != null">
        <div class="current-driver"><span>Attribution actuelle</span><strong>{{ currentDriver ? fullName(currentDriver) : 'Chauffeur #' + badge.driverId }}</strong></div>
        <button type="button" class="outline-button" :disabled="saving" @click="$emit('unassign')">Dissocier ce chauffeur</button>
        <p class="helper">Pour réattribuer ce badge, dissociez-le d'abord.</p>
      </template>
      <template v-else>
        <label class="field"><span>Associer à un chauffeur</span><select v-model="driverId" :disabled="saving || !activeDrivers.length"><option value="">{{ activeDrivers.length ? 'Sélectionner un chauffeur' : 'Aucun chauffeur actif disponible' }}</option><option v-for="driver in activeDrivers" :key="driver.id" :value="String(driver.id)">{{ fullName(driver) }}</option></select></label>
        <button type="button" class="secondary-button" :disabled="saving || !driverId" @click="$emit('assign', driverId)">{{ saving ? 'Association…' : 'Associer le badge' }}</button>
      </template>
    </section>
    <section class="association" aria-labelledby="badge-vehicles-title">
      <h3 id="badge-vehicles-title">Véhicules associés</h3>
      <p class="helper">Un badge peut être associé à plusieurs véhicules.</p>

      <div v-if="badge.balises?.length" class="vehicle-assignments">
        <div v-for="balise in badge.balises" :key="balise.id" class="vehicle-assignment">
          <VehicleMiniLink :balise="balise" />
          <button
            type="button"
            class="vehicle-remove"
            :disabled="saving"
            :aria-label="'Dissocier ' + (balise.name || 'le véhicule')"
            @click="$emit('unassign-vehicle', balise.id)"
          >Dissocier</button>
        </div>
      </div>
      <p v-else class="empty-vehicles">Aucun véhicule associé.</p>

      <p v-if="vehiclesError" class="error" role="alert">
        {{ vehiclesError }}
        <button type="button" class="retry-button" @click="$emit('retry-vehicles')">Réessayer</button>
      </p>
      <template v-else>
        <label class="field">
          <span>Ajouter un véhicule</span>
          <select v-model="vehicleId" :disabled="saving || vehiclesLoading || !availableVehicles.length">
            <option value="">{{ vehiclePlaceholder }}</option>
            <option v-for="vehicle in availableVehicles" :key="vehicle.id" :value="String(vehicle.id)">
              {{ vehicle.name || ('Véhicule #' + vehicle.id) }}
            </option>
          </select>
        </label>
        <button
          type="button"
          class="secondary-button"
          :disabled="saving || vehiclesLoading || !vehicleId"
          @click="addSelectedVehicle"
        >Associer le véhicule</button>
      </template>
    </section>
    <p v-if="error" class="error" role="alert">{{ error }}</p>
  </div>
</template>
<script>
import VehicleMiniLink from '@/components/display/VehicleMiniLink.vue';

export default {
  name:'BadgeEditor',
  components: { VehicleMiniLink },
  props: {
    badge: { type:Object, required:true },
    drivers: { type:Array, required:true },
    vehicles: { type:Array, default:()=>[] },
    vehiclesLoading: { type:Boolean, default:false },
    vehiclesError: { type:String, default:'' },
    saving: { type:Boolean, default:false },
    error: { type:String, default:'' }
  },
  emits: ['save','assign','unassign','assign-vehicle','unassign-vehicle','retry-vehicles','cancel'],
  data(){ return { form:{ref:this.badge.ref ?? '',status:this.badge.status ?? 'NEW'},driverId:'',vehicleId:'' }; },
  computed:{
    changed(){ return this.form.ref !== (this.badge.ref ?? '') || this.form.status !== (this.badge.status ?? 'NEW'); },
    currentDriver(){ return this.drivers.find(d=>String(d.id)===String(this.badge.driverId)) || this.badge.driver || null; },
    activeDrivers(){ return [...this.drivers].filter(d=>d.status==='ACTIVE').sort((a,b)=>`${a.lastName||''} ${a.firstName||''}`.localeCompare(`${b.lastName||''} ${b.firstName||''}`,'fr')); },
    availableVehicles() {
      const associatedIds = new Set((this.badge.balises || []).map(b => String(b.id)));
      return this.vehicles
        .filter(b => !associatedIds.has(String(b.id)))
        .sort((a,b) => String(a.name || '').localeCompare(String(b.name || ''), 'fr'));
    },
    vehiclePlaceholder() {
      if (this.vehiclesLoading) return 'Chargement des véhicules…';
      return this.availableVehicles.length ? 'Sélectionner un véhicule' : 'Aucun autre véhicule disponible';
    }
  },
  methods:{
    formatLastActivity(value) {
      if (!value) return 'Jamais';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '—';
      return date.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    fullName(driver){ return [driver.firstName,driver.lastName].filter(Boolean).join(' ') || `Chauffeur #${driver.id}`; },
    save(){ if(!this.saving && this.changed) this.$emit('save',{ref:this.form.ref.trim() || null,status:this.form.status}); },
    addSelectedVehicle() {
      if (!this.saving && this.vehicleId && this.availableVehicles.some(b => String(b.id) === this.vehicleId)) {
        this.$emit('assign-vehicle', Number(this.vehicleId));
      }
    }
  },
  watch:{ badge:{ deep:true, handler(b){ this.form={ref:b.ref ?? '',status:b.status ?? 'NEW'}; this.vehicleId = ''; } } }
};
</script>
<style scoped>
.editor { padding:26px; color:#17243b; }.editor-heading { display:flex; justify-content:space-between; gap:14px; margin-bottom:27px; }.eyebrow { color:#627c9e; font-size:10px; font-weight:800; letter-spacing:.14em; margin-bottom:7px; }h2 { font-size:20px; margin:0 0 5px; font-weight:750; }.editor-heading p,.helper { font-size:12px; line-height:1.5; color:#7a8799; margin:0; }.close-button { width:32px; height:32px; flex:none; border:1px solid #e2e7ef; border-radius:8px; background:white; color:#60738c; font-size:22px; cursor:pointer; }
.read-only { display:flex; justify-content:space-between; gap:12px; border-bottom:1px solid #edf0f5; padding:11px 0; font-size:13px; }.read-only span { color:#8390a3; }.read-only strong { color:#25354d; overflow-wrap:anywhere; text-align:right; }
.field { display:flex; flex-direction:column; gap:8px; margin-top:18px; color:#33435b; font-size:13px; font-weight:700; }.field input,.field select { width:100%; min-width:0; border:1px solid #d8e1ed; background:white; color:#25354d; border-radius:9px; padding:11px 12px; font-size:13px; font-weight:400; }.field input:focus,.field select:focus { outline:2px solid #93b5e3; outline-offset:1px; }.switch-row { margin-top:21px; background:#f7f9fc; border:1px solid #e8edf4; border-radius:10px; padding:15px; display:flex; align-items:center; justify-content:space-between; gap:14px; }.switch-row span { display:flex; flex-direction:column; gap:4px; }.switch-row strong { font-size:13px; }.switch-row small { font-size:12px; font-weight:400; color:#748297; }.switch-row input { width:20px; height:20px; accent-color:#1b4f95; cursor:pointer; }
.actions { margin-top:20px; }.primary-button,.secondary-button,.outline-button { border-radius:9px; padding:11px 14px; font-size:13px; font-weight:750; cursor:pointer; }.primary-button { background:#1b4f95; border:1px solid #1b4f95; color:white; width:100%; }.secondary-button { background:#1b4f95; color:white; border:1px solid #1b4f95; margin-top:12px; }.outline-button { background:white; color:#a03e3e; border:1px solid #e5c9c9; margin-top:12px; }.association { margin-top:28px; border-top:1px solid #edf0f5; padding-top:23px; }.association h3 { font-size:16px; font-weight:750; margin:0 0 6px; }.current-driver { padding:15px; margin-top:14px; background:#f7f9fc; border:1px solid #e8edf4; border-radius:9px; display:flex; flex-direction:column; gap:6px; }.current-driver span { color:#8290a3; font-size:11px; }.current-driver strong { font-size:14px; }.association .helper:last-child { margin-top:9px; }.error { margin:16px 0 0; background:#fff0f0; border-radius:9px; padding:12px; color:#a32929; font-size:13px; }button:disabled { opacity:.5; cursor:not-allowed; }
.vehicle-assignments { display:flex; flex-direction:column; gap:9px; margin-top:14px; }
.vehicle-assignment { display:flex; align-items:center; justify-content:space-between; gap:10px; min-width:0; }
.vehicle-assignment :deep(.vehicle-mini-link) { flex:1 1 auto; min-width:0; }
.vehicle-remove { flex:none; background:white; color:#a03e3e; border:1px solid #e5c9c9; border-radius:8px; padding:8px 10px; font-size:12px; font-weight:700; cursor:pointer; }
.empty-vehicles { color:#8a96a7; font-size:13px; margin:15px 0 0; }
.retry-button { border:0; background:transparent; text-decoration:underline; color:inherit; cursor:pointer; font-weight:700; }
@media(max-width:620px){.editor{padding:20px 16px;}}
</style>
